import React, { useCallback, useMemo, useState } from 'react';
import { Pressable, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { match } from 'ts-pattern';
import { MActuEntrepriseService } from '../../entities/MActuEntrepriseService';
import { MActuEntrepriseSiteGeo } from '../../entities/MActuEntrepriseSiteGeo';
import { MActuEntrepriseGroup } from '../../entities/MActuEntrepriseGroup';
import { contributionColors } from '../../services/api/contributions';
import { t } from '../../services/translations';
import { SpreadingType } from '../../types';
import { Chip } from './Chip';
import { ContributionEntrepriseGroup } from '../../entities/ContributionEntrepriseGroup';
import { ContributionEntrepriseSiteGeo } from '../../entities/ContributionEntrepriseSiteGeo';
import { ContributionEntrepriseService } from '../../entities/ContributionEntrepriseService';

const colorList = Object.values(contributionColors);

export type SpreadingChipListProps = {
  item: {
    id: number;
    spreadingType: SpreadingType;
    entrepriseServices: (MActuEntrepriseService | ContributionEntrepriseService)[];
    entrepriseSiteGeos: (MActuEntrepriseSiteGeo | ContributionEntrepriseSiteGeo)[];
    entrepriseGroups: (MActuEntrepriseGroup | ContributionEntrepriseGroup)[];
  };
  monocolor?: boolean;
  maxItems?: number;
  chipStyle?: StyleProp<ViewStyle>;
};

export default function SpreadingChipList({ item, monocolor, maxItems, chipStyle }: SpreadingChipListProps) {
  const [moreHovered, setMoreHovered] = useState(false);

  const spreadingItems = useMemo(
    () =>
      match(item)
        .with({ spreadingType: SpreadingType.ENTREPRISE }, () => [t('company_wide')])
        .with({ spreadingType: SpreadingType.USERS }, () => [t('some_users')])
        .with({ spreadingType: SpreadingType.SERVICES }, () =>
          item.entrepriseServices.map((e) => e.entrepriseService.libelle)
        )
        .with({ spreadingType: SpreadingType.SITES }, () =>
          item.entrepriseSiteGeos.map((e) => e.entrepriseSiteGeo.siteGeo)
        )
        .with({ spreadingType: SpreadingType.SITES_AND_SERVICES }, () => [
          ...item.entrepriseServices.map((e) => e.entrepriseService.libelle),
          ...item.entrepriseSiteGeos.map((e) => e.entrepriseSiteGeo.siteGeo),
        ])
        .with({ spreadingType: SpreadingType.GROUPS }, () => item.entrepriseGroups.map((e) => e.entrepriseGroup.label))
        .otherwise(() => [t('company_wide')]),
    [item]
  );

  const limitedSpreadingItems = useMemo(() => {
    if (maxItems === undefined) {
      return spreadingItems;
    }

    if (spreadingItems.length <= maxItems) {
      return spreadingItems;
    } else {
      return spreadingItems.slice(0, maxItems - 1);
    }
  }, [spreadingItems, maxItems]);

  const getLabelColor = useCallback((label: string) => {
    if (label.length === 0) {
      return colorList[0];
    }

    let hash = 0;
    for (let i = 0; i < label.length; i++) {
      const char = label.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }

    return colorList[Math.abs(hash) % colorList.length];
  }, []);

  return (
    <>
      {limitedSpreadingItems.map((item) => (
        <Chip
          text={item}
          style={[styles.chip, chipStyle]}
          color={monocolor ? colorList[0] : getLabelColor(item)}
          small
        />
      ))}
      {maxItems && spreadingItems.length > maxItems && (
        <Pressable onHoverIn={() => setMoreHovered(true)} onHoverOut={() => setMoreHovered(false)}>
          <Chip
            text="..."
            style={[styles.chip, chipStyle]}
            color={monocolor ? colorList[0] : getLabelColor(spreadingItems[spreadingItems.length - 1])}
            small
            selected={moreHovered}
          />
        </Pressable>
      )}
      <View style={styles.moreChipAnchor}>
        {maxItems && moreHovered && (
          <View style={styles.moreChipContainer}>
            {spreadingItems.slice(maxItems - 1).map((item) => (
              <Chip
                text={item}
                style={[styles.chip, chipStyle]}
                color={monocolor ? colorList[0] : getLabelColor(item)}
                small
              />
            ))}
          </View>
        )}
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  chip: {
    margin: 5,
  },
  moreChipAnchor: {
    width: '100%',
    zIndex: 10,
  },
  moreChipContainer: {
    position: 'absolute',
    top: 5,
    gap: 5,
    width: '100%',
    paddingRight: 10,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
});
